import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import { getLoggedInUser, logout } from "../utils/auth";
import { getAdminDetails } from "../services/profileService";
import { GoDotFill } from "react-icons/go";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();

  const user = getLoggedInUser();

  const profileImage =
    user?.logo_url || user?.photo || user?.image || "/assets/images/user.png";

  const [apiLogo, setApiLogo] = useState("");

  const profileName = user?.teacher_name || user?.name || "School Admin";
  const profileEmail = user?.email || "Manage account";

  const logoSrc = apiLogo || profileImage || "/assets/images/logo.png";

  /* ================= LOGO LOAD (CACHED) ================= */
  useEffect(() => {
    const loadProfileLogo = async () => {
      try {
        // ✅ CHECK LOCAL CACHE
        const cachedLogo = localStorage.getItem("app_logo");

        if (cachedLogo) {
          setApiLogo(cachedLogo);
          return;
        }

        // ✅ API CALL ONLY FIRST TIME
        const res = await getAdminDetails();

        const logo =
          res?.profile?.logo_url ||
          res?.profile?.photo ||
          res?.profile?.image ||
          "";

        if (logo) {
          setApiLogo(logo);
          localStorage.setItem("app_logo", logo); // SAVE CACHE
        }
      } catch {
        setApiLogo("");
      }
    };

    loadProfileLogo();
  }, []);

  /* ================= SIDEBAR DROPDOWN ================= */
  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = "0px";
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(
          ".sidebar-submenu li a"
        );
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
          }
        });
      });
    };

    openActiveDropdown();

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const handleOverlayClick = (e) => {
    if (e.target.tagName === "SECTION" || e.target.classList.contains("overlay")) {
      setMobileMenu(false);
    }
  };

  return (
    <section 
      className={mobileMenu ? "overlay active" : "overlay"}
      onClick={handleOverlayClick}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className={
          sidebarActive
            ? "sidebar active"
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>

        {/* ===== LOGO ===== */}
        <div>
          <Link to="/" className="sidebar-logo">
            <img
              src={logoSrc}
              alt="site logo"
              className="light-logo"
              onError={(e) => {
                e.currentTarget.src = "/assets/images/logo.png";
              }}
            />
            <img
              src={logoSrc}
              alt="site logo"
              className="dark-logo"
              onError={(e) => {
                e.currentTarget.src = "/assets/images/logo.png";
              }}
            />
            <img
              src={logoSrc}
              alt="site logo"
              className="logo-icon"
              onError={(e) => {
                e.currentTarget.src = "/assets/images/logo.png";
              }}
            />
          </Link>
        </div>

        {/* ===== SIDEBAR ===== */}
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink to="/" className={(nav) => (nav.isActive ? "active-page" : "")}>
                <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                <span>Dashboard</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/school-timings"
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:clock-outline" className="menu-icon" />
                <span>School Timings</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/classes-management"
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:google-classroom" className="menu-icon" />
                <span>Class Management</span>
              </NavLink>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:calendar-check-outline" className="menu-icon" />
                <span>Attendance Management</span>
              </Link>
              <ul className="sidebar-submenu">
                <li>
                  <NavLink to="/attendance/dashboard" className={({ isActive }) => isActive ? "active-page" : ""}>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Attendance Dashboard</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/attendance/classwise" className={({ isActive }) => isActive ? "active-page" : ""}>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>Class-wise Attendance</span>
                  </NavLink>
                </li>
                <li>
                  {/* <NavLink to="/attendance/reports" className={({ isActive }) => isActive ? "active-page" : ""}>
                    <GoDotFill className="circle-icon text-info-600" />
                    <span>Attendance Reports</span>
                  </NavLink> */}
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="mdi:account-group-outline" className="menu-icon" />
                <span>Student Management</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink to="/add-student-page" className={({ isActive }) => isActive ? "active-page" : ""}>
                    <GoDotFill className="circle-icon text-primary-600" />
                    <span>Add New Student</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/student-page-list" className={({ isActive }) => isActive ? "active-page" : ""}>
                    <GoDotFill className="circle-icon text-success-600" />
                    <span>List of Students</span>
                  </NavLink>
                </li>
              </ul>
            </li>

            <li>
              <NavLink
                to="/teacher-management"
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:account-tie-outline" className="menu-icon" />
                <span>Teacher Management</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/files-management"
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:folder-outline" className="menu-icon" />
                <span>Files Management</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/my-devices"
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <Icon icon="solar:devices-bold-duotone" className="menu-icon" />
                <span>My Devices</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </aside>

      {/* ===== MAIN ===== */}
      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header shadow-sm border-bottom">
          <div className="row align-items-center justify-content-between w-100 m-0">
            <div className="col-auto px-0">
              <div className="d-flex align-items-center gap-4">
                <button type="button" className="sidebar-toggle" onClick={sidebarControl}>
                  {sidebarActive ? (
                    <Icon icon="iconoir:arrow-right" className="icon text-2xl" />
                  ) : (
                    <Icon icon="heroicons:bars-3-solid" className="icon text-2xl" />
                  )}
                </button>

                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>

            {/* PROFILE */}
            <div className="col-auto px-0">
              <div className="dropdown">
                <button
                  className="topbar-profile-trigger d-flex align-items-center gap-2 border-0 bg-transparent p-0 shadow-none"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ outline: 'none' }}
                >
                  <div className="profile-avatar-wrapper d-flex align-items-center">
                    <img
                      src={profileImage}
                      alt="avatar"
                      className="w-40-px h-40-px rounded-circle object-fit-cover border border-2 border-white shadow-sm"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextSibling.style.setProperty("display", "flex", "important");
                      }}
                    />
                    <div
                      className="w-40-px h-40-px rounded-circle bg-primary text-white align-items-center justify-content-center fw-bold shadow-sm"
                      style={{ display: profileImage ? "none" : "flex" }}
                    >
                      {profileName ? profileName.charAt(0).toUpperCase() : "A"}
                    </div>
                  </div>
                  <div className="topbar-profile-meta d-none d-md-flex text-start flex-column">
                    <strong className="text-dark fw-bold" style={{ fontSize: "14px", lineHeight: "1.2" }}>{profileName}</strong>
                    <small className="text-muted" style={{ fontSize: "11px", lineHeight: "1.2" }}>{profileEmail}</small>
                  </div>
                  <Icon icon="lucide:chevron-down" className="text-muted d-none d-md-block" fontSize="16px" />
                </button>

                <div className="dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-4 p-3 mt-2" style={{ minWidth: "240px" }}>
                  <div className="d-flex align-items-center gap-3 pb-3 mb-2 border-bottom">
                    <div className="w-48-px h-48-px rounded-circle bg-primary-600 text-white d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ fontSize: "18px" }}>
                      {profileName ? profileName.charAt(0) : "A"}
                    </div>
                    <div className="d-flex flex-column text-start">
                      <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>{profileName}</h6>
                      <span className="text-muted" style={{ fontSize: "11px", wordBreak: "break-all" }}>{profileEmail}</span>
                    </div>
                  </div>
                  <ul className="d-flex flex-column gap-1 m-0 p-0">
                    <li>
                      <Link className="dropdown-item rounded-3 d-flex align-items-center gap-2 py-2 text-secondary" to="/view-profile">
                        <Icon icon="solar:user-bold-duotone" className="text-primary-600" fontSize="18px" />
                        <span>My Profile</span>
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item rounded-3 d-flex align-items-center gap-2 py-2 text-secondary" to="/my-devices">
                        <Icon icon="solar:devices-bold-duotone" className="text-primary-600" fontSize="18px" />
                        <span>My Devices</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-3 d-flex align-items-center gap-2 py-2 text-danger"
                        onClick={(e) => {
                          e.preventDefault();
                          localStorage.removeItem("app_logo"); // ✅ CLEAR CACHE
                          logout();
                        }}
                      >
                        <Icon icon="solar:logout-3-bold-duotone" className="text-danger" fontSize="18px" />
                        <span>Log Out</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="dashboard-main-body">{children}</div>

        {/* FOOTER */}
        <footer className="d-footer">
          <p>© 2025 CRM System. All Rights Reserved.</p>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;